import React from "react";
import { Box, Stack, Typography, Button, CircularProgress } from "@mui/material";

// Brand accent reused from the Login screen.
export const ORANGE = "rgba(232, 108, 0, 1)";
export const ORANGE_HOVER = "rgba(200, 93, 0, 1)";

/** Full-screen dark shell with a centered card — matches the login aesthetic. */
export function FlowShell({ children }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "var(--app-bg, #0f1115)",
        px: 3,
        py: 4,
      }}
    >
      <Stack
        sx={{
          width: "100%",
          maxWidth: 440,
          gap: 2.5,
          backgroundColor: "var(--surface-1, #16181d)",
          border: "1px solid var(--border-1, rgba(255,255,255,0.08))",
          borderRadius: "16px",
          p: { xs: 3, md: 4 },
        }}
      >
        {children}
      </Stack>
    </Box>
  );
}

export function FlowHeader({ title, subtitle, icon }) {
  return (
    <Stack alignItems="center" gap={1.5}>
      {icon && (
        <Stack
          sx={{
            width: 56,
            height: 56,
            borderRadius: "14px",
            backgroundColor: "rgba(232,108,0,0.12)",
            alignItems: "center",
            justifyContent: "center",
            color: ORANGE,
          }}
        >
          {icon}
        </Stack>
      )}
      <Typography
        sx={{ fontSize: 20, fontWeight: 700, color: "var(--text-1)", textAlign: "center" }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          sx={{ fontSize: 14, color: "var(--text-2)", textAlign: "center", lineHeight: 1.5 }}
        >
          {subtitle}
        </Typography>
      )}
    </Stack>
  );
}

export function PrimaryButton({ loading, children, sx, ...props }) {
  return (
    <Button
      fullWidth
      variant="contained"
      disabled={loading || props.disabled}
      sx={{
        height: 46,
        borderRadius: "10px",
        textTransform: "none",
        fontSize: 15,
        fontWeight: 700,
        backgroundColor: ORANGE,
        boxShadow: "0 4px 16px rgba(232, 108, 0, 0.3)",
        "&:hover": { backgroundColor: ORANGE_HOVER },
        "&.Mui-disabled": { backgroundColor: "rgba(232,108,0,0.4)", color: "rgba(255,255,255,0.6)" },
        ...sx,
      }}
      {...props}
    >
      {loading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : children}
    </Button>
  );
}

export function SecondaryButton({ children, sx, ...props }) {
  return (
    <Button
      fullWidth
      variant="text"
      sx={{
        textTransform: "none",
        fontSize: 14,
        fontWeight: 600,
        color: "var(--text-2)",
        "&:hover": { color: "var(--text-1)", backgroundColor: "transparent" },
        ...sx,
      }}
      {...props}
    >
      {children}
    </Button>
  );
}

/**
 * True when a webauthn request failed because the pre-auth ticket expired /
 * is missing (backend returns response_message "PREAUTH_EXPIRED"). The whole
 * flow must restart from the password step when this happens.
 */
export function isPreauthExpired(error) {
  const data = error && error.response && error.response.data;
  const code = data && (data.response_message || data.detail);
  return code === "PREAUTH_EXPIRED";
}

// Backend enforcement codes for the one-device-only policy → user messages.
const CODE_MESSAGES = {
  CREDENTIAL_EXISTS:
    "This account already has a registered device. An administrator must remove it before you can register a new one.",
  SYNCED_CREDENTIAL_REJECTED:
    "This device's passkey syncs across devices, which isn't allowed here. Use a device-bound passkey or a security key.",
  DEVICE_FINGERPRINT_MISMATCH:
    "You can only sign in from your registered device. Contact an administrator to move to a new device.",
};

/** Maps a raw ceremony/API error to a user-readable message. */
export function humanizeError(error) {
  const name = error && (error.name || error.code);
  if (name === "NotAllowedError") return "The request was cancelled or timed out. Please try again.";
  if (name === "InvalidStateError") return "This browser is already registered.";
  const data = error && error.response && error.response.data;
  const code = data && (data.response_message || data.detail);
  if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];
  const status = error && error.response && error.response.status;
  if (status === 401) return "Verification failed. Please try again.";
  if (status === 403) return "You are not permitted to do that.";
  if (status === 409) return "That request is no longer valid. Please restart.";
  if (status === 429) return "Too many attempts. Please wait a moment and try again.";
  const detail = error && error.response && error.response.data && error.response.data.message;
  return detail || "Something went wrong. Please try again.";
}
